<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('siteCheckinOut.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('siteCheckinOut.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-12">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('siteCheckinOut.criteria') }}</legend>
                  <div class="row">
                    <div class="col-md-3">
                      <div class="form-group">
                        <label class="required">{{
                          $t('siteCheckinOut.checkInDate')
                        }}</label>
                        <a-range-picker
                          :allowClear="false"
                          format="DD-MM-YYYY"
                          v-model="model.dateRange"
                          value-format="DD-MM-YYYY"
                          :ranges="{
                            Today: [moment(), moment()],
                            'This Month': [
                              moment().startOf('month'),
                              moment().endOf('month')
                            ]
                          }"
                          class="ts-w-full"
                        >
                          <template slot="renderExtraFooter"></template>
                        </a-range-picker>
                        <div class="validate" v-if="errors.has('dateRange')">
                          {{ errors.first('dateRange') }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>{{ $t('siteCheckinOut.siteProfile') }}</label>
                        <a-select
                          v-model="model.site_profile_id"
                          :options="sites"
                          showSearch
                          :filterOption="filterOption"
                          placeholder="All"
                          mode="multiple"
                          class="ts-w-full"
                          :max-tag-count="1"
                        >
                        </a-select>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>{{ $t('siteCheckinOut.employee') }}</label>
                        <a-select
                          v-model="model.employee_id"
                          :options="owners"
                          showSearch
                          :filterOption="filterOption"
                          placeholder="--select--"
                          mode="multiple"
                          class="ts-w-full"
                          :max-tag-count="1"
                          @change="handleMilestoneOwnerChange"
                        >
                        </a-select>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-9 ts-flex ts-justify-end ts-space-x-2">
                      <ts-button @click="onPreview" color="primary">{{
                        $t('preview')
                      }}</ts-button>
                      <ts-button @click="exportExcel" color="info">{{
                        $t('excel')
                      }}</ts-button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper></ts-card
        >
      </div>
      <div class="col-md-12 ts-mt-3">
        <ts-card>
          <ts-table :columns="columns" :records="resources" :loading="loading">
            <template v-slot="{ record, index }">
              <td class="ts-whitespace-no-wrap">{{ index + 1 }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.name }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.uniquied }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.site_name }}</td>
              <td class="ts-whitespace-no-wrap">
                <span
                  class="ts-px-2 ts-rounded-md ts-bg-blue-100 ts-text-blue-500"
                  >{{ record.check_in_date }}</span
                >
              </td>
              <td class="ts-whitespace-no-wrap">
                <a
                  :href="
                    `https://maps.google.com/maps?q=${record.in_lat +
                      ',' +
                      record.in_lng}`
                  "
                  target="_blank"
                >
                  {{ record.in_lat + ',' + record.in_lng }}
                </a>
              </td>
              <td class="ts-whitespace-no-wrap">
                <a
                  :href="record.photo_check_in"
                  target="_blank"
                  v-if="record.photo_check_in"
                >
                  <img
                    class="ts-h-10 ts-rounded"
                    v-lazy="{
                      src: record.photo_check_in,
                      loading: require('@/assets/Rolling-1s-200px_2.svg')
                    }"
                  />
                  <!-- <img
                    :src="record.photo_check_in"
                    class="ts-h-10 ts-rounded-md"
                  /> -->
                </a>
              </td>
              <td class="ts-whitespace-no-wrap">
                <span
                  v-if="record.out_lat"
                  class="ts-px-2 ts-rounded-md ts-bg-orange-100 ts-text-orange-500"
                  >{{ record.check_out_date }}</span
                >
              </td>
              <td class="ts-whitespace-no-wrap">
                <a
                  :href="
                    `https://maps.google.com/maps?q=${record.out_lat +
                      ',' +
                      record.out_lng}`
                  "
                  target="_blank"
                  v-if="record.out_lat"
                >
                  {{ record.out_lat + ',' + record.out_lng }}
                </a>
              </td>
              <td class="ts-whitespace-no-wrap">
                <a
                  :href="record.photo_check_out"
                  target="_blank"
                  v-if="record.photo_check_out"
                >
                  <img
                    class="ts-h-10 ts-rounded"
                    v-lazy="{
                      src: record.photo_check_out,
                      loading: require('@/assets/Rolling-1s-200px_2.svg')
                    }"
                  />
                </a>
              </td>
            </template>
          </ts-table>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { sumBy, trim } from 'lodash'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'

export default {
  name: 'siteCheckinOut',
  data() {
    return {
      loading: false,
      errors: new Errors(),
      sites: [],
      owners: [],
      tasks: [],
      resources: [],
      model: {
        dateRange: [
          moment()
            .startOf('month')
            .format('DD-MM-YYYY'),
          moment()
            .endOf('month')
            .format('DD-MM-YYYY')
        ],
        site_profile_id: undefined,
        employee_id: undefined
      }
    }
  },
  computed: {
    columns() {
      return [
        { name: this.$t('siteCheckinOut.numeric'), style: 'width: 12px' },
        { name: this.$t('siteCheckinOut.userName'), style: 'width: 7%' },
        { name: this.$t('siteCheckinOut.uniqueid') },
        { name: this.$t('siteCheckinOut.siteName') },
        { name: this.$t('siteCheckinOut.checkinAt') },
        { name: this.$t('siteCheckinOut.checkInLocation') },
        { name: this.$t('siteCheckinOut.photo') },
        { name: this.$t('siteCheckinOut.checkoutAt') },
        { name: this.$t('siteCheckinOut.checkOutLocation') },
        { name: this.$t('siteCheckinOut.photo') }
      ]
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    moment,
    sumBy,
    ...mapActions('report/project', [
      'getSite',
      'getOwner',
      'siteCheckInOut',
      'siteCheckInOutExcel'
    ]),
    async fetchData() {
      try {
        this.loading = true
        const [siteResponse, ownerResponse] = await Promise.all([
          this.getSite(),
          this.getOwner()
        ])

        this.sites = siteResponse.map(el => ({
          value: el.id,
          label: el.site_name
        }))

        this.owners = ownerResponse.map(el => ({
          value: el.employee_id,
          label: el.employee_name_en
        }))
      } catch (err) {
        this.$toasted.error(err.message)
      } finally {
        this.loading = false
      }
    },
    onPreview() {
      this.loading = true
      this.siteCheckInOut(this.model)
        .then(result => {
          this.resources = result.data
        })
        .catch(err => {
          this.$toasted.error(err.message)
          this.errors = new Errors(err.errors)
        })
        .finally(() => (this.loading = false))
    },
    exportExcel() {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/project/siteCheckInOutExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    }
  }
}
</script>
